import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
  faDesktop, faServer, faDatabase, faChartBar, faRandom,
  faIndustry, faClock, faDollarSign
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/ProductionLineResilience-Process.png";
import vis from "../../assets/images/use-cases/ProductionLineResilience-Dashboard.gif";

const ProductionLineResilience = () => {
  return (
      <>
        <SEO
            title="Production Line Resilience: AI Digital Twin for Manufacturing | Nebulyx AI"
            description="Simulate equipment failures offline to create optimized response procedures before incidents occur. Reduce MTTR by 34% through Data Object Graph modeling and scenario analysis."
            keywords="AI digital twin, production line resilience, manufacturing simulation, MTTR optimization, offline simulation, procedure optimization"
            canonicalUrl="https://nebulyx.ai/use-cases/production-line-resilience"
            ogType="article"
        />

        <article className="use-case-page">
          {/* Hero */}
          <header className="use-case-hero">
            <Container>
              <Row>
                <Col className={"w-100"}>
                  <div className={"w-100 text-center"}>
                    <div >
                      <Badge className="sector-badge m-1 mb-4 h-100">
                        <FontAwesomeIcon icon={faIndustry}/>
                        &nbsp;
                        Manufacturing
                      </Badge>
                      <h1 className="use-case-title">Question:&nbsp;How do we minimize time to fix when the line goes down?</h1>
                    </div>
                  </div>
                  <div>
                    <p className="use-case-subtitle w-100 text-center">
                      Simulate production line failures offline to design and test new procedures, spare parts positioning, and crew responses-before incidents ever occur.
                    </p>
                  </div>
                </Col>
              </Row>

              <Row className="hero-stats-inline ">
                <Col className="stat-item-inline">
                  <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                  <span className="stat-value">34%</span>
                  <span className="stat-label">MTTR Reduction</span>
                </Col>
                <Col  className="stat-item-inline">
                  <FontAwesomeIcon icon={faDollarSign} className="stat-icon"/>
                  <span className="stat-value">$4.2M</span>
                  <span className="stat-label">Annual Savings</span>
                </Col>
                <Col  className="stat-item-inline">
                  <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                  <span className="stat-value">10,000+</span>
                  <span className="stat-label">Scenarios Simulated</span>
                </Col>
              </Row>

            </Container>
          </header>

          {/* Executive Summary */}
          <section className="exec-summary-section">
            <Container>
              <h2 className="section-title">Executive Summary</h2>
              <Row className="g-4">
                <Col md={4}>
                  <Card className="summary-card h-100">
                    <Card.Body>
                      <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                          sty icon={faExclamationTriangle}/></span>The Problem</h3>
                      <ul className="summary-list">
                        <li>Every minute of downtime costs $20,000+</li>
                        <li>Response procedures designed after failures occur</li>
                        <li>No way to test new processes without production risk</li>
                        <li>Spare parts and crew positioning based on gut feel</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="summary-card h-100">
                    <Card.Body>
                      <h3 className={"text-start"}><span
                          className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                          sty icon={faLightbulb}/></span>The Solution</h3>
                      <ul className="summary-list">
                        <li>Model production line as Data Object Graph</li>
                        <li>Overlay failure and repair analytics</li>
                        <li>Simulate line breaks offline</li>
                        <li>Generate optimized procedures from scenarios</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="summary-card h-100">
                    <Card.Body>
                      <h3 className={"text-start"}><span
                          className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                          sty icon={faRocket}/></span>The Benefits</h3>
                      <ul className="summary-list">
                        <li>Test new procedures without production risk</li>
                        <li>34% reduction in Mean Time to Repair</li>
                        <li>Data-driven spare parts positioning</li>
                        <li>Crew scheduling optimized per failure type</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </section>

          {/* Process Steps */}
          <section className="content-section process-section">
            <Container>
              <h2 className="section-title">The AI Digital Twin Process</h2>
              <p className="text-center text-muted mb-4">
                Connect production data, model the line as a Data Object Graph, overlay analytics, then run offline simulations to create and validate new response procedures.
              </p>
              <div className="process-steps-container">
                <div className="process-step-item">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <strong>Connect Data</strong>
                    <span>Equipment sensors, MTBF records, maintenance logs, spare parts inventory, crew schedules</span>
                  </div>
                </div>
                <div className="step-arrow">
                  <FontAwesomeIcon icon={faArrowRight}/>
                </div>
                <div className="process-step-item">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <strong>Model the Line</strong>
                    <span>Data Object Graph maps equipment topology, dependencies, and failure cascade paths</span>
                  </div>
                </div>
                <div className="step-arrow">
                  <FontAwesomeIcon icon={faArrowRight}/>
                </div>
                <div className="process-step-item">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <strong>Overlay Analytics</strong>
                    <span>MTBF prediction, failure probability, repair time estimates, cascade impact scoring</span>
                  </div>
                </div>
                <div className="step-arrow">
                  <FontAwesomeIcon icon={faArrowRight}/>
                </div>
                <div className="process-step-item">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <strong>Scenario Analysis</strong>
                    <span>Simulate line breaks offline, test response strategies, generate new procedures</span>
                  </div>
                </div>
              </div>
            </Container>
          </section>

          {/* AI Digital Twin - Two Sections */}
          <section className="content-section twin-section">
            <Container>
              <h2 className="section-title">The AI Digital Twin</h2>
              <Row className="g-4">
                {/* Back-end Architecture */}
                <Col lg={6}>
                  <Card className="twin-view-card h-100">
                    <Card.Body>
                      <div className="twin-view-header">
                        <div className="twin-view-icon backend">
                          <FontAwesomeIcon icon={faServer}/>
                        </div>
                        <h3>Digital Twin Process Flow</h3>
                      </div>
                      <p className="twin-view-description">
                        Connects equipment, maintenance, and inventory data to build a Data Object Graph of the production line.
                        Overlays failure analytics to predict cascade paths. Enables offline simulation of line breaks
                        so teams can design and validate new procedures before deploying to the production floor.</p>
                      <div className="image-parent">
                        <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Front-end UI */}
                <Col lg={6}>
                  <Card className="twin-view-card h-100">
                    <Card.Body>
                      <div className="twin-view-header">
                        <div className="twin-view-icon frontend">
                          <FontAwesomeIcon icon={faDesktop}/>
                        </div>
                        <h3>Offline Simulation Workbench</h3>
                      </div>
                      <p className="twin-view-description">
                        Visualizes the production line topology and allows teams to inject simulated failures at any point.
                        Tests different response procedures, spare parts strategies, and crew configurations offline-then
                        measures MTTR, cost impact, and cascade effects before committing to operational changes.</p>
                      <div className="image-parent">
                        <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              {/* Twin Components */}
              <Row className="g-4 mt-4">
                <Col md={4}>
                  <Card className="twin-card h-100">
                    <Card.Body>
                      <div className="twin-card-header">
                        <div className="twin-icon">
                          <FontAwesomeIcon icon={faDatabase}/>
                        </div>
                        <h4>Data Analysed</h4>
                      </div>
                      <ul className="twin-list">
                        <li>Equipment Sensor Feeds</li>
                        <li>MTBF / Reliability Records</li>
                        <li>Historical Maintenance Logs</li>
                        <li>Spare Parts Inventory</li>
                        <li>Technician Skills Matrix</li>
                        <li>Production Line Topology</li>
                        <li>Downtime Cost Models</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="twin-card h-100">
                    <Card.Body>
                      <div className="twin-card-header">
                        <div className="twin-icon">
                          <FontAwesomeIcon icon={faChartBar}/>
                        </div>
                        <h4>AI / Data Products</h4>
                      </div>
                      <ul className="twin-list">
                        <li>Failure probability prediction</li>
                        <li>Cascade path analysis</li>
                        <li>MTTR optimization model</li>
                        <li>Spare parts positioning optimizer</li>
                        <li>Procedure effectiveness scoring</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="twin-card h-100">
                    <Card.Body>
                      <div className="twin-card-header">
                        <div className="twin-icon">
                          <FontAwesomeIcon icon={faRandom}/>
                        </div>
                        <h4>Scenarios Tested</h4>
                      </div>
                      <ul className="twin-list">
                        <li>Inject failure at any equipment</li>
                        <li>Multi-point cascade simulation</li>
                        <li>Compare response procedures</li>
                        <li>Test spare parts positioning</li>
                        <li>Crew shift configurations</li>
                        <li>Maintenance window timing</li>
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Discovery */}
              <div className="discovery-highlight mt-5">
                <Row className="align-items-center">
                  <Col lg={8}>
                    <h3>The Discovery</h3>
                    <p>
                      By modelling the production line as a <strong>Data Object Graph</strong> and simulating 10,000 failure scenarios offline,
                      we make discoveries like - pre-positioning just 4 critical spare parts at the production floor-instead of central warehouse-
                      <strong> reduces average MTTR from 52 minutes to 31 minutes</strong>.
                      Offline simulations can reveal that large percentage (eg 70%) of extended repairs are caused by parts retrieval time,
                      an insight impossible to gain without testing procedures before incidents occur.
                    </p>
                  </Col>
                  <Col lg={4}>
                    <div className="discovery-stat">
                      <span className="discovery-label">Optimal Strategy Found</span>
                      <span className="discovery-value">Spare Parts Pre-positioning</span>
                      <span className="discovery-note">MTTR 52→31 min, tested offline first</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </Container>
          </section>

          {/* CTA */}
          <section className="cta-section">
            <Container>
              <div className="cta-content">
                <h2>Want to Design Better Procedures Before the Line Goes Down?</h2>
                <p>
                  If you're defining response procedures reactively and every minute of downtime costs thousands,
                  let's build your Digital Twin together.
                </p>
                <div className="cta-buttons">
                  <Button
                      variant="light"
                      size="lg"
                      href="https://cal.eu/jon-cooke/discovery-call"
                      className="me-3 mb-2"
                  >
                    Book a Discovery Call
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                  </Button>
                  <Link to="/use-cases">
                    <Button variant="outline-light" size="lg" className="mb-2">
                      View More Use Cases
                    </Button>
                  </Link>
                </div>
              </div>
            </Container>
          </section>
        </article>
      </>
  );
};

export default ProductionLineResilience;
